import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import {
  Button,
  Checkbox,
  Select,
  Separator,
  Table,
  TableBody,
  TableDataCell,
  TableHead,
  TableHeadCell,
  TableRow,
  TextInput,
  Toolbar,
  Window,
  WindowContent,
  WindowHeader,
  ProgressBar,
} from 'react95';
import Draggable from 'react-draggable';
import { t } from './i18n';
import WindowQuote from './WindowQuote';

// All available columns definition: [field, i18n key, default visible]
const ALL_COLUMNS = [
  ['type', '', true],
  ['brand', 'shopBrand', true],
  ['model', 'shopModel', true],
  ['grade', 'shopGrade', true],
  ['cpu', 'shopCpu', true],
  ['ram', 'shopRam', true],
  ['storage_gb', 'shopStorage', true],
  ['battery', 'shopBattery', true],
  ['price_ttc', 'shopPrice', true],
  ['color', 'shopColor', false],
  ['gpu', 'shopGpu', false],
  ['os', 'shopOs', false],
];

function WindowShop({ close, centerWindows, zIndex, isFocused, onFocus, lang }) {
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [selected, setSelected] = useState({});
  const [sortField, setSortField] = useState('price_ttc');
  const [sortDir, setSortDir] = useState('asc');
  const [showQuote, setShowQuote] = useState(false);
  const [opacity, setOpacity] = useState(0);
  const [openMenu, setOpenMenu] = useState(null); // 'filters' | 'columns' | null
  const [visibleCols, setVisibleCols] = useState(() =>
    new Set(ALL_COLUMNS.filter(c => c[2]).map(c => c[0]))
  );
  const [filters, setFilters] = useState({
    name: '',
    brand: 'all',
    ram: 'all',
    storage: 'all',
    grade: 'all',
    color: 'all',
    cpu: 'all',
    battery: 'all',
    gpu: 'all',
    os: 'all',
  });
  const menuRef = useRef(null);

  const doCenter = useCallback(() => {
    centerWindows();
    setTimeout(() => centerWindows(), 50);
  }, [centerWindows]);

  useEffect(() => {
    doCenter();
    setTimeout(() => {
      doCenter();
      setOpacity(1);
    }, 150);
    fetchCatalog();
    // eslint-disable-next-line
  }, []);

  // Close menu on outside click
  useEffect(() => {
    if (!openMenu) return;
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpenMenu(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [openMenu]);

  const fetchCatalog = () => {
    setLoading(true);
    setError(false);
    fetch('shop/api.php')
      .then(r => {
        if (!r.ok) throw new Error('Network error');
        return r.json();
      })
      .then(data => {
        setCatalog(Array.isArray(data) ? data : []);
        setLoading(false);
        setTimeout(() => doCenter(), 50);
      })
      .catch(() => {
        setError(true);
        setLoading(false);
      });
  };

  const gradeLabel = (item) => {
    return lang === 'fr' ? (item.grade_fr || item.grade) : (item.grade_en || item.grade);
  };

  const uniqueValues = useMemo(() => {
    const fields = ['brand', 'ram', 'storage', 'grade', 'color', 'cpu', 'battery', 'gpu', 'os'];
    const result = {};
    fields.forEach(f => {
      const vals = [...new Set(
        catalog.map(item => {
          if (f === 'grade') return gradeLabel(item);
          return item[f];
        }).filter(v => v !== null && v !== undefined && v !== '')
      )];
      vals.sort((a, b) => {
        if (typeof a === 'number' && typeof b === 'number') return a - b;
        return String(a).localeCompare(String(b));
      });
      result[f] = vals;
    });
    return result;
    // eslint-disable-next-line
  }, [catalog, lang]);

  const filteredCatalog = useMemo(() => {
    let result = catalog.filter(item => {
      if (filters.name && !(item.model || item.name).toLowerCase().includes(filters.name.toLowerCase())) return false;
      if (filters.brand !== 'all' && item.brand !== filters.brand) return false;
      if (filters.ram !== 'all' && String(item.ram) !== filters.ram) return false;
      if (filters.storage !== 'all' && item.storage !== filters.storage) return false;
      if (filters.grade !== 'all' && gradeLabel(item) !== filters.grade) return false;
      if (filters.color !== 'all' && item.color !== filters.color) return false;
      if (filters.cpu !== 'all' && item.cpu !== filters.cpu) return false;
      if (filters.battery !== 'all' && item.battery !== filters.battery) return false;
      if (filters.gpu !== 'all' && item.gpu !== filters.gpu) return false;
      if (filters.os !== 'all' && item.os !== filters.os) return false;
      return true;
    });

    result.sort((a, b) => {
      let va = a[sortField];
      let vb = b[sortField];
      if (typeof va === 'number' && typeof vb === 'number') {
        return sortDir === 'asc' ? va - vb : vb - va;
      }
      va = String(va || '').toLowerCase();
      vb = String(vb || '').toLowerCase();
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [catalog, filters, sortField, sortDir]);

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const toggleSelect = (sku) => {
    setSelected(prev => ({ ...prev, [sku]: !prev[sku] }));
  };

  const toggleSelectAll = () => {
    const allChecked = filteredCatalog.length > 0 && filteredCatalog.every(item => selected[item.sku]);
    const newSel = { ...selected };
    filteredCatalog.forEach(item => { newSel[item.sku] = !allChecked; });
    setSelected(newSel);
  };

  const selectedItems = catalog.filter(item => selected[item.sku]);

  const handleQuoteRequest = () => {
    if (selectedItems.length === 0) {
      alert(t('shopNoSelection', lang));
      return;
    }
    setShowQuote(true);
  };

  const setFilter = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const sortIndicator = (field) => {
    if (sortField !== field) return '';
    return sortDir === 'asc' ? ' ▲' : ' ▼';
  };

  const makeFilterOptions = (field) => {
    return [
      { value: 'all', label: t('shopFilterAll', lang) },
      ...(uniqueValues[field] || []).map(v => ({
        value: String(v),
        label: field === 'ram' ? v + ' Go' : String(v)
      }))
    ];
  };

  const toggleCol = (field) => {
    setVisibleCols(prev => {
      const next = new Set(prev);
      if (next.has(field)) next.delete(field);
      else next.add(field);
      return next;
    });
  };

  const activeColumns = ALL_COLUMNS.filter(c => visibleCols.has(c[0]));

  const renderCell = (item, field) => {
    switch (field) {
      case 'type':
        return (
          <TableDataCell key={field} style={{ textAlign: 'center' }}>
            {item.type === 'apple'
              ? <i className="fa-brands fa-apple"></i>
              : <i className="fa-brands fa-windows"></i>}
          </TableDataCell>
        );
      case 'brand':
        return <TableDataCell key={field}>{item.brand}</TableDataCell>;
      case 'model':
        return <TableDataCell key={field}>{item.model || item.name}</TableDataCell>;
      case 'ram':
        return <TableDataCell key={field}>{item.ram ? item.ram + ' Go' : ''}</TableDataCell>;
      case 'storage_gb':
        return <TableDataCell key={field}>{item.storage}</TableDataCell>;
      case 'grade':
        return <TableDataCell key={field}>{gradeLabel(item)}</TableDataCell>;
      case 'price_ttc':
        return (
          <TableDataCell key={field} style={{ whiteSpace: 'nowrap' }}>
            <strong>{item.price_ttc.toFixed(2)} € TTC</strong><br />
            <small>{item.price_ht.toFixed(2)} € HT</small><br />
            <small>{t('shopEcotax', lang)} : {item.ecotax.toFixed(2)} €</small>
          </TableDataCell>
        );
      default:
        return <TableDataCell key={field}>{item[field]}</TableDataCell>;
    }
  };

  const hasActiveFilter = Object.entries(filters).some(([k, v]) => k === 'name' ? v !== '' : v !== 'all');
  const allChecked = filteredCatalog.length > 0 && filteredCatalog.every(item => selected[item.sku]);
  const someChecked = filteredCatalog.some(item => selected[item.sku]);

  const renderFilterRow = (field, labelKey) => (
    <div key={field} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '2px 0' }}>
      <span style={{ minWidth: 80, flexShrink: 0 }}>{t(labelKey, lang)}</span>
      <Select
        defaultValue='all'
        options={makeFilterOptions(field)}
        onChange={(opt) => setFilter(field, opt.value)}
        width={180}
        menuMaxHeight={200}
      />
    </div>
  );

  return (
    <>
      <Draggable bounds="parent" handle=".draggable-title">
        <Window style={{ opacity, zIndex }} className='window center-window shop-window' onMouseDownCapture={onFocus}>
          <WindowHeader className={`window-title${isFocused && !showQuote ? '' : ' inactive'}`}>
            <div className="float-right">
              <Button onClick={close}>&times;</Button>
            </div>
            <div className="draggable-title">
              <span className="Computer_16x16_4 window-icon"></span>
              <span> {t('shopTitle', lang)}</span>
            </div>
          </WindowHeader>

          <Toolbar style={{ padding: '2px 4px', flexShrink: 0 }} ref={menuRef}>
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <Button
                variant='menu'
                size='sm'
                active={openMenu === 'filters'}
                onClick={() => setOpenMenu(openMenu === 'filters' ? null : 'filters')}
              >
                {t('shopFilters', lang)}{hasActiveFilter ? ' ●' : ''}
              </Button>
              {openMenu === 'filters' && (
                <div className="shop-dropdown" style={{ position: 'absolute', left: 0, top: '100%', zIndex: 10 }}>
                  <div style={{ padding: 8, minWidth: 300 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '2px 0' }}>
                      <span style={{ minWidth: 80, flexShrink: 0 }}>{t('shopModel', lang)}</span>
                      <TextInput
                        value={filters.name}
                        onChange={e => setFilter('name', e.target.value)}
                        placeholder={t('shopModel', lang)}
                        width={180}
                      />
                    </div>
                    {renderFilterRow('brand', 'shopBrand')}
                    {renderFilterRow('ram', 'shopRam')}
                    {renderFilterRow('storage', 'shopStorage')}
                    {renderFilterRow('grade', 'shopGrade')}
                    {renderFilterRow('color', 'shopColor')}
                    {renderFilterRow('cpu', 'shopCpu')}
                    {renderFilterRow('battery', 'shopBattery')}
                    {renderFilterRow('gpu', 'shopGpu')}
                    {renderFilterRow('os', 'shopOs')}
                  </div>
                </div>
              )}
            </div>
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <Button
                variant='menu'
                size='sm'
                active={openMenu === 'columns'}
                onClick={() => setOpenMenu(openMenu === 'columns' ? null : 'columns')}
              >
                {t('shopColumns', lang)}
              </Button>
              {openMenu === 'columns' && (
                <div className="shop-dropdown" style={{ position: 'absolute', left: 0, top: '100%', zIndex: 10 }}>
                  <div style={{ padding: 8, minWidth: 160 }}>
                    {ALL_COLUMNS.filter(c => c[1]).map(([field, labelKey]) => (
                      <label key={field} className="shop-col-toggle" style={{ display: 'flex', alignItems: 'center', padding: '2px 0' }}>
                        <Checkbox
                          checked={visibleCols.has(field)}
                          onChange={() => toggleCol(field)}
                        />
                        <span style={{ marginLeft: 4 }}>{t(labelKey, lang)}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <Separator orientation='vertical' size='35px' />
            <Button
              variant='menu'
              size='sm'
              onClick={handleQuoteRequest}
              disabled={selectedItems.length === 0}
            >
              {t('shopQuoteBtn', lang)} {selectedItems.length > 0 && `(${selectedItems.length})`}
            </Button>
          </Toolbar>

          <WindowContent style={{ padding: 4, flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            {loading ? (
              <div style={{ textAlign: 'center', padding: 32 }}>
                <ProgressBar variant='tile' value={100} />
                <p className="top-8">{t('shopLoading', lang)}</p>
              </div>
            ) : error ? (
              <div style={{ textAlign: 'center', padding: 32 }}>
                <p>{t('shopError', lang)}</p>
                <Button onClick={fetchCatalog} className="top-8">{t('shopRetry', lang)}</Button>
              </div>
            ) : (
              <div className="overflow-table" style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableHeadCell style={{ width: 30 }}>
                        <Checkbox
                          checked={allChecked}
                          indeterminate={someChecked && !allChecked}
                          onChange={toggleSelectAll}
                        />
                      </TableHeadCell>
                      {activeColumns.map(([field, labelKey]) => (
                        <TableHeadCell
                          key={field}
                          onClick={() => toggleSort(field)}
                          style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
                        >
                          {labelKey ? t(labelKey, lang) : ''}{sortIndicator(field)}
                        </TableHeadCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredCatalog.length === 0 ? (
                      <TableRow>
                        <TableDataCell colSpan={activeColumns.length + 1} style={{ textAlign: 'center' }}>
                          {t('shopEmpty', lang)}
                        </TableDataCell>
                      </TableRow>
                    ) : filteredCatalog.map(item => (
                      <TableRow key={item.sku}>
                        <TableDataCell style={{ textAlign: 'center' }}>
                          <Checkbox
                            checked={!!selected[item.sku]}
                            onChange={() => toggleSelect(item.sku)}
                          />
                        </TableDataCell>
                        {activeColumns.map(([field]) => renderCell(item, field))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </WindowContent>
        </Window>
      </Draggable>
      {showQuote && (
        <WindowQuote
          items={selectedItems}
          close={() => setShowQuote(false)}
          centerWindows={centerWindows}
          zIndex={(zIndex || 0) + 1}
          isFocused={true}
          lang={lang}
        />
      )}
    </>
  );
}

export default WindowShop;
